/**
 * v3.admin.products — Wave 6.1 WRITE-ROUTES-PRODUCTS typed admin write surface
 * for the Products / Features / CardTypes domain.
 *
 * Replaces the previous editor pattern of building MERGE blobs client-side
 * and POSTing to `/v3/admin/run-seed-sql`. Each catalog gets a typed
 * upsert/delete pair on the wire (POST/DELETE) and a typed sub-client here.
 *
 * Routes (server: PCBJxChangeApi/Functions/AdminProductsFunction.cs):
 *   POST   /v3/admin/products
 *   DELETE /v3/admin/products/{code}
 *   POST   /v3/admin/product-features
 *   DELETE /v3/admin/product-features/{productCode}/{featureCode}
 *   POST   /v3/admin/product-customer-types
 *   DELETE /v3/admin/product-customer-types/{productCode}/{customerType}
 *   POST   /v3/admin/product-customer-types/replace      ← bulk allow-list
 *   POST   /v3/admin/features
 *   DELETE /v3/admin/features/{code}
 *   POST   /v3/admin/card-types
 *   DELETE /v3/admin/card-types/{code}
 *
 * All routes require the `x-config-seed-bypass: <bypassKey>` header (sent
 * via the first positional arg, identical to the IRA admin sub-clients).
 *
 * The class is composed onto `AdminApi.products` from `client.ts` (a new
 * ctor argument was NOT added to AdminApi to keep this wave's surface
 * isolated from the existing v3-admin.ts file, per Wave 6.1 brief).
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import { buildAdminAuthHeaders } from '../admin-auth.js';
import type { ApiHttpClient } from '../http.js';
import type { AdminWriteResult } from './admin.js';

// ─────────────────────────────────────────────────────────────────────────
// Body shapes — camelCase JSON wire format consumed by the Function layer.
// ─────────────────────────────────────────────────────────────────────────

export interface ProductUpsertBody {
  code: string;
  /** JHA product code sent on AcctAdd. Required by the server on every save. */
  jhaProdCode: string;
  name: string;
  description?: string | null;
  category: string;            // DDA | SAV | CD | IRA | BOX | Service
  acctType: string;
  glCode: string;
  callClassCode: string;
  rateCode?: string | null;
  stmtMsgCode?: string | null;
  cardTypeCode?: string | null;
  minAge?: number | null;
  maxAge?: number | null;
  minimumDeposit?: number | null;
  requiresGuardian?: boolean;
  allowJoints?: boolean;
  maxJointOwners?: number;
  allowBeneficiaries?: boolean;
  maxBeneficiaries?: number;
  allowDebitCard?: boolean;
  allowCardLink?: boolean;
  allowNetTeller?: boolean;
  allowOdp?: boolean;
  allowRegE?: boolean;
  businessOnly?: boolean;
  personalOnly?: boolean;
  sortOrder?: number | null;
  isActive?: boolean;
}

export interface ProductFeatureUpsertBody {
  productCode: string;
  featureCode: string;
  state: string;               // Unavailable | Available | Recommended | Required
  isActive?: boolean;
}

export interface ProductCustomerTypeUpsertBody {
  productCode: string;
  customerType: string;
}

export interface ProductCustomerTypeReplaceBody {
  productCode: string;
  customerTypes: ReadonlyArray<string>;
}

export interface FeatureUpsertBody {
  code: string;
  displayName: string;
  categoryCode: string;
  subCategory?: string | null;
  sortOrder?: number | null;
  coreField?: string | null;
  coreAction?: string | null;
  hasToggle?: boolean;
  hasAmount?: boolean;
  isFollowUp?: boolean;
  followUpDays?: number | null;
  requiresDocument?: boolean;
  isCrossSell?: boolean;
  isActive?: boolean;
}

export interface CardTypeUpsertBody {
  code: string;
  description: string;
  binPrefix?: string;          // empty for visual design rows (BACKLOG-0873)
  prodCode?: string;
  isoType?: string;            // card network; empty for design rows
  cardCategory: string;        // Personal | Business | ATM
  issuerBankCode?: string | null;
  isActive?: boolean;
  sortOrder?: number | null;
  /** Card-design artwork (BACKLOG-0873). Omit to leave existing untouched on a
   *  metadata edit; "" clears it. */
  imageBase64?: string;
  imageContentType?: string;
}

// Compliance-owned product catalogs (server: AdminComplianceFunction.cs,
// RequiredRole=Risk). Routes:
//   POST   /v3/admin/product-service-availability
//   DELETE /v3/admin/product-service-availability/{productKey}/{serviceKey}
//   POST   /v3/admin/product-funding-method-availability
//   DELETE /v3/admin/product-funding-method-availability/{productKey}/{fundingMethodKey}
//   POST   /v3/admin/product-summary-chip-config
//   DELETE /v3/admin/product-summary-chip-config/{productKey}/{chipKey}
// Shapes verified against Models/AdminComplianceModels.cs (2026-06-14).

export interface ProductServiceAvailabilityUpsertBody {
  productKey: string;
  serviceKey: string;
  behavior: string;            // Unavailable | Available | Recommended | Required
  defaultValue?: boolean | null;
  subConfigJson?: string | null;
  citationKey: string;         // REQUIRED — FK to ProcedureCitations
  isActive?: boolean;
}

export interface ProductFundingMethodAvailabilityUpsertBody {
  productKey: string;
  fundingMethodKey: string;
  behavior: string;            // Unavailable | Available | Preferred | Required
  displayOrder?: number | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  defaultAmount?: number | null;
  citationKey: string;         // REQUIRED — FK to ProcedureCitations
  isActive?: boolean;
}

export interface ProductSummaryChipConfigUpsertBody {
  productKey: string;
  chipKey: string;
  displayOrder?: number | null;
  visible?: boolean | null;
  labelOverride?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
export interface ProductBulkChange {
  code: string;
  isActive?: boolean;
  sortOrder?: number;
  minimumDeposit?: number;
}

// Sub-clients (one per catalog) — mirror the iraTransactionCodes /
// iraInstitutionRelationships / iraContributionPhaseouts pattern in
// v3-admin.ts.
// ─────────────────────────────────────────────────────────────────────────

class ProductsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: ProductUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/products', { ...opts, headers, body });
  }

  /** Bulk partial edit (BACKLOG-1752): only the fields given per product are changed. */
  public bulk(
    bypassKey: string,
    body: { changes: ReadonlyArray<ProductBulkChange> },
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/products/bulk', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    code: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/products/${encodeURIComponent(code)}`,
      { ...opts, headers }
    );
  }
}

class ProductFeaturesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: ProductFeatureUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/product-features', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    productCode: string,
    featureCode: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/product-features/${encodeURIComponent(productCode)}/${encodeURIComponent(featureCode)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

class ProductCustomerTypesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: ProductCustomerTypeUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/product-customer-types', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    productCode: string,
    customerType: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/product-customer-types/${encodeURIComponent(productCode)}/${encodeURIComponent(customerType)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }

  /**
   * Bulk-replace one product's customer-type allow-list. Used by the matrix
   * editor where many cells are toggled for one product before saving.
   */
  public replace(
    bypassKey: string,
    body: ProductCustomerTypeReplaceBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/product-customer-types/replace', {
      ...opts,
      headers,
      body,
    });
  }
}

class FeaturesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: FeatureUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/features', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    code: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/features/${encodeURIComponent(code)}`,
      { ...opts, headers }
    );
  }
}

class CardTypesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: CardTypeUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/card-types', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    code: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.del<AdminWriteResult>(
      `/v3/admin/card-types/${encodeURIComponent(code)}`,
      { ...opts, headers }
    );
  }
}

class ProductServiceAvailabilityAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: ProductServiceAvailabilityUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/product-service-availability', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    productKey: string,
    serviceKey: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/product-service-availability/${encodeURIComponent(productKey)}/${encodeURIComponent(serviceKey)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

class ProductFundingMethodsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: ProductFundingMethodAvailabilityUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/product-funding-method-availability', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    productKey: string,
    fundingMethodKey: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/product-funding-method-availability/${encodeURIComponent(productKey)}/${encodeURIComponent(fundingMethodKey)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

class ProductSummaryChipsAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upsert(
    bypassKey: string,
    body: ProductSummaryChipConfigUpsertBody,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/product-summary-chip-config', { ...opts, headers, body });
  }

  public remove(
    bypassKey: string,
    productKey: string,
    chipKey: string,
    opts?: RequestOptions
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path = `/v3/admin/product-summary-chip-config/${encodeURIComponent(productKey)}/${encodeURIComponent(chipKey)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

/**
 * Composite — exposed at `client.retail.admin.products`. Each property is a
 * sub-client matching one editor's catalog table.
 *
 * Wired into the AdminApi instance from `client.ts` after construction
 * (no edit to v3-admin.ts itself, per the Wave 6.1 brief).
 */
export class AdminProductsApi {
  public readonly products: ProductsAdminApi;
  public readonly productFeatures: ProductFeaturesAdminApi;
  public readonly productCustomerTypes: ProductCustomerTypesAdminApi;
  public readonly features: FeaturesAdminApi;
  public readonly cardTypes: CardTypesAdminApi;
  public readonly productServiceAvailability: ProductServiceAvailabilityAdminApi;
  public readonly productFundingMethods: ProductFundingMethodsAdminApi;
  public readonly productSummaryChips: ProductSummaryChipsAdminApi;

  public constructor(http: ApiHttpClient) {
    this.products = new ProductsAdminApi(http);
    this.productFeatures = new ProductFeaturesAdminApi(http);
    this.productCustomerTypes = new ProductCustomerTypesAdminApi(http);
    this.features = new FeaturesAdminApi(http);
    this.cardTypes = new CardTypesAdminApi(http);
    this.productServiceAvailability = new ProductServiceAvailabilityAdminApi(http);
    this.productFundingMethods = new ProductFundingMethodsAdminApi(http);
    this.productSummaryChips = new ProductSummaryChipsAdminApi(http);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend AdminApi with `.products` so callers can
// write `client.retail.admin.products.products.upsert(...)` etc. without
// importing AdminProductsApi by name.
// ─────────────────────────────────────────────────────────────────────────

declare module './admin.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface AdminApi {
    /** Wave 6.1 — Products / Features / CardTypes typed write surface. */
    readonly products: AdminProductsApi;
  }
}
